import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Theater } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AdminListPage } from '@/features/admin/components/AdminListPage'
import { useProductions } from '../hooks/useProductions'
import type { ProductionStatus } from '@/types'

const STATUS_LABEL: Record<ProductionStatus, string> = {
  planning: 'Planning',
  intake_open: 'Intake Open',
  curating: 'Curating',
  confirmed: 'Confirmed',
  completed: 'Completed',
}

const STATUS_VARIANT: Record<ProductionStatus, 'default' | 'secondary' | 'outline'> = {
  planning: 'outline',
  intake_open: 'default',
  curating: 'default',
  confirmed: 'secondary',
  completed: 'outline',
}

export function AdminProductionsPage() {
  const { productions, loading, error } = useProductions()
  const [showArchived, setShowArchived] = useState(false)

  const visible = useMemo(
    () => productions.filter((p) => showArchived || !p.isArchived),
    [productions, showArchived]
  )

  return (
    <AdminListPage
      title="Productions"
      loading={loading}
      error={error}
      bare
      action={
        <Button asChild>
          <Link to="/admin/productions/new">
            <Plus className="mr-1 h-4 w-4" />
            New Production
          </Link>
        </Button>
      }
      filters={
        <div className="flex items-center gap-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
            Show archived
          </Label>
        </div>
      }
    >
      {visible.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <Theater className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-lg font-semibold">No productions yet</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Productions are ODD-hosted events — festivals, showcases, concerts — where
              artists apply to participate. Create one to start the intake and curation
              flow.
            </p>
            <Button asChild className="mt-2">
              <Link to="/admin/productions/new">
                <Plus className="mr-1 h-4 w-4" />
                New Production
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => (
            <Link key={p.id} to={`/admin/productions/${p.id}`} className="block">
              <Card className="transition-colors hover:bg-muted/40">
                <CardHeader className="space-y-1 pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{p.name}</CardTitle>
                    <Badge variant={STATUS_VARIANT[p.status]} className="shrink-0">
                      {STATUS_LABEL[p.status]}
                    </Badge>
                  </div>
                  <CardDescription className="text-xs">
                    {p.venue} · {formatDate(p.startDate)}
                    {p.endDate ? ` – ${formatDate(p.endDate)}` : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">/{p.slug}</span>
                  {p.isArchived && <Badge variant="outline">Archived</Badge>}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </AdminListPage>
  )
}

function formatDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
